// TODO: Rename file and functions ti useGDPRBanner
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { GDPR_PREFERENCES_COOKIE } from '@wkda/gdprlib';

import { PTAPIService, PTCookieSerice } from './pTypes';
import { createSubmitHandlers } from './createSubmitHandlers';
import * as helpers from './helpers';
import { categories } from './constants';

/**
 * @typedef {string | {[key: string]: unknown}} Cookies
 */

/**
 * @typedef {Object} CookieOptions
 * @property {Date} expires
 * @property {string} path
 * @property {string} sameSite
 */

/**
 * @typedef {Object} CookieService
 * @property {(key: string) => string | null} get
 * @property {(key: string, value: unknown, options: CookieOptions) => void} set
 * @property {() => Cookies} getAll
 * cookie value or it will break the `@wkda/gdprlib` parser
 */

/**
 * @typedef {Object} GDPRBannerSelectionState
 * @property {boolean} essential
 * @property {boolean} analytical
 * @property {boolean} marketing
 */

/**
 * @typedef {Object} APIService
 * @property {<T>(url: string, data: T) => Promise<void>} post
 */

/**
 * @typedef {Object} GDPRBannerState
 * @property {GDPRBannerSelectionState} state
 * @property {(key: string) => () => void} toggleSelection
 * @property {() => Promise<void>} submit
 * @property {() => Promise<void>} submitAll
 * @property {boolean} isEverythingSelected
 */

/**
 * @type {GDPRBannerSelectionState}
 */
const defaultSelectionState = {
  [categories.ESSENTIAL]: true,
  [categories.ANALYTICAL]: false,
  [categories.MARKETING]: false,
};

/**
 * @type {GDPRBannerState}
 */
const defaultState = {
  state: defaultSelectionState,
  toggle: () => () => {},
  submit: () => Promise.resolve(),
};

const BannerStateContext = React.createContext(defaultState);

/**
 * @param {GDPRBannerSelectionState} [initialState]
 */
function useGDPRBannerState(initialState = defaultSelectionState) {
  const [bannerState, setBannerState] = React.useState(initialState);
  const toggleSelection = (key) => () =>
    setBannerState((prevState) =>
      key === categories.ESSENTIAL
        ? prevState
        : {
            ...prevState,
            [key]: !prevState[key],
          },
    );
  const selectAll = () =>
    Object.keys(bannerState).reduce(
      (acc, key) => ({
        ...acc,
        [key]: true,
      }),
      {},
    );

  return {
    state: bannerState,
    toggleSelection,
    setBannerState,
    selectAll,
    isEverythingSelected: helpers.isEverythingSelected(bannerState),
  };
}

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} props.isProd
 * @param {APIService} props.APIService
 * @param {CookieService} props.cookieService
 * @param {() => void} [props.onSubmit]
 */
export function GDPRBannerStateProvider({
  children,
  isProd,
  APIService,
  cookieService,
  onSubmit,
}) {
  const [shouldDisplayBanner, setShouldDisplayBanner] = React.useState(true);
  const getCurrentSelection = () => {
    const { categories: preferences = [] } =
      cookieService.get(GDPR_PREFERENCES_COOKIE) || {};

    return Object.keys(defaultSelectionState).reduce(
      (acc, key) => ({
        ...acc,
        [key]: preferences.includes(key) || key === categories.ESSENTIAL,
      }),
      {},
    );
  };
  const {
    state,
    toggleSelection,
    selectAll,
    setBannerState,
    isEverythingSelected,
  } = useGDPRBannerState(getCurrentSelection());
  const { submit } = createSubmitHandlers({
    APIService,
    cookieService,
    isProd,
    onSubmit,
  });
  const submitAll = async () => {
    const selectionState = selectAll();
    setBannerState(selectionState);
    await submit(selectionState);

    // This will allow user to see the selection update
    setTimeout(() => setShouldDisplayBanner(false), 100);
  };
  const submitSelected = () => submit(state);

  return (
    <BannerStateContext.Provider
      value={{
        state,
        toggleSelection,
        submit: submitSelected,
        submitAll,
        isEverythingSelected,
      }}
    >
      {shouldDisplayBanner ? children : null}
    </BannerStateContext.Provider>
  );
}

GDPRBannerStateProvider.propTypes = {
  children: PropTypes.node.isRequired,
  isProd: PropTypes.bool.isRequired,
  APIService: PTAPIService.isRequired,
  cookieService: PTCookieSerice.isRequired,
  onSubmit: PropTypes.func,
};

export function useGDPRBannerContext() {
  const context = React.useContext(BannerStateContext);

  return context;
}
